import { AgxFormDropdown, AgxFormTextField } from '@urbanx/agx.forms';
import {
  AgxButton,
  AgxSpacer,
  AgxTypography,
  type DropdownOption,
} from '@urbanx/agx.ui-components2';
import { RexListingRefreshResultStatus } from '@urbanx/agx.rex.client';
import Panel from 'components/panel/Panel';
import { RexListingRefreshForm } from './rexListingRefreshFormDefinition';
import { useRexListingRefresh } from './useRexListingRefresh';

interface RexListingRefreshProps {
  agencyOptions: DropdownOption[];
}

export default function RexListingRefresh({ agencyOptions }: RexListingRefreshProps) {
  const {
    form,
    request,
    result,
    isRefreshing,
    isUnresolved,
    isCheckingOutcome,
    isCheckingStatus,
    requestError,
    statusError,
    canCheckStatus,
    canRetrySameRequest,
    checkStatus,
    retrySameRequest,
  } = useRexListingRefresh();

  return (
    <Panel title="Refresh one listing">
      <AgxSpacer gap="spacing-lg" fill>
        <AgxTypography id="rex-refresh-description" type="body" size="small">
          Refresh a connected campaign’s listing data directly from REX. This updates Autopilot’s
          cache; website publishing remains a separate action.
        </AgxTypography>
        <RexListingRefreshForm.Root form={form}>
          <AgxSpacer gap="spacing-lg" fill>
            <RexListingRefreshForm.Field
              name="agencyId"
              Input={AgxFormDropdown}
              label="Agency"
              disabled={isUnresolved || isRefreshing}
              options={[{ value: '', label: 'Select agency' }, ...agencyOptions]}
              placeholder="Select agency"
              searchable
              searchPlaceholder="Search agencies"
              stretch
            />
            <RexListingRefreshForm.Field
              name="listingId"
              Input={AgxFormTextField}
              label="REX listing ID"
              disabled={isUnresolved || isRefreshing}
              type="text"
              stretch
            />
            <RexListingRefreshForm.Submit>
              {({ submit, canSubmit }) => (
                <AgxButton
                  id="rex-refresh-submit"
                  variant="primary"
                  text={isRefreshing ? 'Refreshing' : 'Refresh listing'}
                  disabled={!canSubmit || isUnresolved || isRefreshing}
                  onClick={() => void submit()}
                />
              )}
            </RexListingRefreshForm.Submit>
          </AgxSpacer>
        </RexListingRefreshForm.Root>
        {requestError && <div role="alert">{requestError}</div>}
        {request && (
          <AgxSpacer gap="spacing-md" fill>
            <AgxTypography id="rex-refresh-operation" type="body" size="small">
              Operation ID: {request.operationId}
            </AgxTypography>
            {isCheckingOutcome && (
              <div role="status">{isRefreshing ? 'Refreshing listing…' : 'Checking status…'}</div>
            )}
            {isUnresolved && !isRefreshing && (
              <AgxTypography id="rex-refresh-recovery" type="body" size="small">
                Waiting for a confirmed result. Check status before starting another refresh.
              </AgxTypography>
            )}
            {result && (
              <AgxSpacer
                gap="spacing-sm"
                role={
                  result.status === RexListingRefreshResultStatus.Failed ||
                  result.status === RexListingRefreshResultStatus.Interrupted
                    ? 'alert'
                    : 'status'
                }
              >
                <span>Saved result for this refresh operation</span>
                <strong>
                  {result.propertyAddress} — {result.status}
                </strong>
                <span>{result.message ?? 'Refresh is running. Check status for the result.'}</span>
              </AgxSpacer>
            )}
            {statusError && <div role="alert">{statusError}</div>}
            {canCheckStatus && (
              <AgxSpacer direction="horizontal" gap="spacing-md">
                <AgxButton
                  id="rex-refresh-status"
                  variant="hollow"
                  text={isCheckingStatus ? 'Checking status…' : 'Check status'}
                  disabled={isCheckingStatus}
                  onClick={() => void checkStatus()}
                />
                {canRetrySameRequest && (
                  <AgxButton
                    id="rex-refresh-retry-request"
                    variant="hollow"
                    text="Retry same request"
                    onClick={() => void retrySameRequest()}
                  />
                )}
              </AgxSpacer>
            )}
          </AgxSpacer>
        )}
      </AgxSpacer>
    </Panel>
  );
}
